import { useLayoutEffect, useRef, useState, type CSSProperties } from "react"
import type { ColDef, ICellRendererParams } from "../../../components/ui/table"
import { collectUniqueScalarValues } from "../../../components/shared/GridColumnFilterPanel"
import type {
  PlatformUserRow,
  RecentLogin,
  UserColumnFilterKey,
} from "../models/types"
import { RECENT_LOGINS_DISPLAY_LIMIT, USER_COLUMN_FILTER_LABELS, USER_MERCHANT_NAME_HEADER } from '../constants/constants';
import { Badge } from "../../../components/ui/badge"
import { Button } from "../../../components/ui/button"
import { Switch } from "../../../components/ui/switch"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../components/ui/tooltip"
import StatusBadge from "../../../components/shared/StatusBadge"
import { cn } from "../../../lib/utils"
import {
  CopyableEmailText,
  GRID_HINT_TOOLTIP_CLASS,
  GridCellWrap,
  accentAvatarStyle,
  nameAvatarInitials,
} from "../../../components/shared/agGridCells"
import {
  BsBoxArrowInRight,
  BsChevronRight,
  BsClock,
  BsGlobe,
  BsPencil,
} from "react-icons/bs"

export type UserGridRow =
  | {
      rowType: "user"
      rowKey: string
      realIdx: number
      createdAt?: string | null
      updatedAt?: string | null
      createdBy?: string | null
      updatedBy?: string | null
    }
  | {
      rowType: "detail"
      rowKey: string
      logins?: RecentLogin[]
      loginsLoading: boolean
      loginsError: string | null
    }

export type UserGridContext = {
  users: PlatformUserRow[]
  authUserId?: number
  expandedRowKey: string | null
  togglingUserKey: string | null
  togglingSandboxKey: string | null
  impersonatingKey: string | null
  recentLoginsByKey: Record<string, RecentLogin[]>
  recentLoginsLoadingKey: string | null
  recentLoginsError: string | null
  accentColors: string[]
  roleStyle: (roleName: string) => { bg: string; color: string }
  isSuperAdminRow: (user: PlatformUserRow) => boolean
  formatRelativeTime: (value?: string | null) => string
  onEdit: (idx: number) => void
  onToggleStatus: (idx: number) => void
  onToggleSandbox: (idx: number) => void
  onImpersonate: (user: PlatformUserRow) => void
  onToggleExpand: (user: PlatformUserRow) => void
}

function getUserRow(params: ICellRendererParams<UserGridRow>) {
  const ctx = params.context as UserGridContext
  const rowKey = params.data?.rowKey
  if (!rowKey || params.data?.rowType !== "user") return null
  const user = ctx.users.find((u) => `${u.userType}-${u.id}` === rowKey)
  const realIdx = params.data.realIdx
  return user ? { user, ctx, realIdx } : null
}

function userFilterValueGetter(
  params: { data?: UserGridRow; context?: unknown },
  pick: (user: PlatformUserRow) => string,
) {
  if (params.data?.rowType !== "user") return ""
  const ctx = params.context as UserGridContext | undefined
  const user = ctx?.users.find(
    (row) => `${row.userType}-${row.id}` === params.data?.rowKey,
  )
  return user ? pick(user) : ""
}

export function UserNameCellRenderer(params: ICellRendererParams<UserGridRow>) {
  const row = getUserRow(params)
  if (!row) return null
  const { user, ctx, realIdx } = row
  const rowKey = `${user.userType}-${user.id}`
  const expanded = ctx.expandedRowKey === rowKey
  const initials = nameAvatarInitials(user.name)
  const accent = ctx.accentColors[(realIdx >= 0 ? realIdx : 0) % ctx.accentColors.length]

  return (
    <GridCellWrap>
      <div className="acq-name-cell um-user-name-cell">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn("h-7 w-7 shrink-0 transition-transform", expanded && "rotate-90")}
          onClick={(event) => {
            event.stopPropagation()
            ctx.onToggleExpand(user)
          }}
          aria-label={expanded ? "Collapse recent logins" : "Expand recent logins"}
          aria-expanded={expanded}
        >
          <BsChevronRight className="h-4 w-4" />
        </Button>
        <div className="acq-avatar" style={accentAvatarStyle(accent)}>
          {initials}
        </div>
        <div className="acq-name truncate text-muted-foreground">{user.name}</div>
      </div>
    </GridCellWrap>
  )
}

export function UserEmailCellRenderer(params: ICellRendererParams<UserGridRow>) {
  const row = getUserRow(params)
  if (!row) return null
  return (
    <GridCellWrap>
      <CopyableEmailText email={row.user.email} />
    </GridCellWrap>
  )
}

export function UserRoleCellRenderer(params: ICellRendererParams<UserGridRow>) {
  const row = getUserRow(params)
  if (!row) return null
  const role = row.ctx.roleStyle(row.user.role)
  return (
    <Badge
      variant="outline"
      className="border-0 font-medium"
      style={{ background: role.bg, color: role.color }}
    >
      {row.user.role}
    </Badge>
  )
}

export function UserStatusCellRenderer(params: ICellRendererParams<UserGridRow>) {
  const row = getUserRow(params)
  if (!row) return null
  return <StatusBadge status={row.user.status} />
}

export function UserLastLoginCellRenderer(params: ICellRendererParams<UserGridRow>) {
  const row = getUserRow(params)
  if (!row) return null
  return (
    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
      <BsClock className="h-3.5 w-3.5 shrink-0 opacity-70" /> {row.user.lastLoginRelative}
    </span>
  )
}

export function UserMerchantNameCellRenderer(params: ICellRendererParams<UserGridRow>) {
  const row = getUserRow(params)
  if (!row) return null
  const merchantName = row.user.merchantName?.trim()
  return (
    <span className="text-muted-foreground">
      {merchantName && merchantName !== '—' ? merchantName : '—'}
    </span>
  )
}

export function UserActionsCellRenderer(params: ICellRendererParams<UserGridRow>) {
  const row = getUserRow(params)
  if (!row) return null

  const { user, ctx, realIdx } = row
  const rowKey = `${user.userType}-${user.id}`
  const locked = ctx.isSuperAdminRow(user)
  // const isSelf = ctx.authUserId === user.id && user.userType === "ADMIN"
  const toggling = ctx.togglingUserKey === rowKey
  const impersonating = ctx.impersonatingKey === rowKey
  // const expanded = ctx.expandedRowKey === rowKey

  return (
    <div
      className="flex h-full flex-wrap items-center gap-1 py-1"
      onClick={(e) => e.stopPropagation()}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="tm-compact-icon-btn shrink-0"
            aria-label="Edit user"
            onClick={() => ctx.onEdit(realIdx)}
            disabled={locked || toggling}
          >
            <BsPencil />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {locked ? "Super admin accounts cannot be edited here" : "Edit user"}
        </TooltipContent>
      </Tooltip>
      {/* <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <Button
              variant="outline"
              size="icon"
              className={cn(
                "tm-compact-icon-btn shrink-0",
                user.status === "Active"
                  ? "hover:border-destructive hover:text-destructive hover:bg-destructive/10"
                  : "hover:border-emerald-500 hover:text-emerald-600 hover:bg-emerald-500/10"
              )}
              aria-label={
                user.status === "Active" ? "Deactivate user" : "Activate user"
              }
              onClick={() => ctx.onToggleStatus(realIdx)}
              disabled={locked || isSelf || toggling || user.status === "Pending"}
            >
              {user.status === "Active" ? <BsPersonSlash /> : <BsPersonCheck />}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {toggling
            ? "Saving…"
            : locked
              ? "Super admin accounts cannot be deactivated"
              : isSelf
                ? "You cannot deactivate your own account"
                : user.status === "Pending"
                  ? "Pending users cannot be toggled here"
                  : user.status === "Active"
                    ? "Deactivate user"
                    : "Activate user"}
        </TooltipContent>
      </Tooltip> */}
      {user.userType === "MERCHANT"  && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-2 rounded-md border px-2 py-1">
              <span className="text-muted-foreground">
                {user.sandboxModeEnabled ? "Sandbox" : "Live"}
              </span>
              <Switch
                checked={!user.sandboxModeEnabled}
                disabled={ctx.togglingSandboxKey === rowKey}
                onCheckedChange={() => ctx.onToggleSandbox(realIdx)}
                aria-label={
                  user.sandboxModeEnabled ? "Disable sandbox mode" : "Enable sandbox mode"
                }
              />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            {user.sandboxModeEnabled
              ? "Sandbox mode is on payments auto-simulate when no acquirer is assigned"
              : "Enable sandbox mode to auto-simulate payments when no acquirer is assigned"}
          </TooltipContent>
        </Tooltip>
      )}
      {(user.userType === "MERCHANT" || user.userType === "ADMIN") && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="tm-compact-icon-btn shrink-0"
              aria-label={
                user.status !== "Active"
                  ? "Login as user (inactive)"
                  : `Login as ${user.name}`
              }
              onClick={() => ctx.onImpersonate(user)}
              disabled={impersonating || user.status !== "Active"}
            >
              <BsBoxArrowInRight />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {impersonating
              ? "Signing in…"
              : user.status !== "Active"
                ? "Only active users can be signed into"
                : `Login as ${user.name}`}
          </TooltipContent>
        </Tooltip>
      )}
      {/* <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn("h-8 w-8 transition-transform", expanded && "rotate-90")}
            onClick={() => ctx.onToggleExpand(user)}
            aria-label={expanded ? "Collapse row" : "Expand row"}
          >
            <BsChevronRight />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {expanded ? "Collapse recent logins" : "Expand recent logins"}
        </TooltipContent>
      </Tooltip> */}
    </div>
  )
}

const DETAIL_ROW_MAX_HEIGHT = 180

function getDisplayLogins(logins?: RecentLogin[]): RecentLogin[] {
  if (!logins?.length) return []

  return [...logins]
    .sort(
      (a, b) =>
        new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
    )
    .slice(0, RECENT_LOGINS_DISPLAY_LIMIT)
}

export function UserNameOrDetailCellRenderer(params: ICellRendererParams<UserGridRow>) {
  if (params.data?.rowType === "detail") {
    return null
  }
  return <UserNameCellRenderer {...params} />
}

export function getUserDetailRowHeight(data?: UserGridRow): number | undefined {
  if (data?.rowType !== "detail") return undefined

  const headerHeight = 52
  const itemHeight = 32
  const minHeight = 88

  if (data.loginsLoading || data.loginsError || !data.logins?.length) {
    return minHeight
  }

  const items = Math.min(getDisplayLogins(data.logins).length, RECENT_LOGINS_DISPLAY_LIMIT)
  return Math.min(headerHeight + items * itemHeight, DETAIL_ROW_MAX_HEIGHT + 16)
}

const RECENT_LOGIN_TIME_COL_ID = "email"

function TruncatedText({ text, className }: { text: string; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null)
  const [truncated, setTruncated] = useState(false)
  const [open, setOpen] = useState(false)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return

    const check = () => {
      setTruncated(el.scrollWidth > el.clientWidth + 1)
    }

    check()
    const observer = new ResizeObserver(check)
    observer.observe(el)
    if (el.parentElement) observer.observe(el.parentElement)
    return () => observer.disconnect()
  }, [text])

  return (
    <Tooltip open={truncated && open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <span ref={ref} className={className}>
          {text}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" align="start" className={GRID_HINT_TOOLTIP_CLASS}>
        {text}
      </TooltipContent>
    </Tooltip>
  )
}

function alignRecentLoginTime(panel: HTMLElement, onLeft: (left: number | null) => void) {
  const rowEl = panel.querySelector<HTMLElement>(".um-recent-logins-list li")
  const headerCell = panel
    .closest(".ag-root")
    ?.querySelector<HTMLElement>(`.ag-header-cell[col-id="${RECENT_LOGIN_TIME_COL_ID}"]`)
  if (!rowEl || !headerCell) {
    onLeft(null)
    return
  }

  const padLeft = Number.parseFloat(window.getComputedStyle(headerCell).paddingLeft) || 0
  const left = Math.round(
    headerCell.getBoundingClientRect().left + padLeft - rowEl.getBoundingClientRect().left,
  )
  onLeft(left)
}

export function UserDetailCellRenderer(params: ICellRendererParams<UserGridRow>) {
  const ref = useRef<HTMLDivElement>(null)
  const isPinned = Boolean(params.pinned)
  const ctx = params.context as UserGridContext
  const row = params.data
  const isDetailRow = row?.rowType === "detail"
  const logins = isDetailRow ? getDisplayLogins(row.logins) : undefined
  const loginsLoading = isDetailRow ? row.loginsLoading : false
  const loginsError = isDetailRow ? row.loginsError : null
  const [timeLeft, setTimeLeft] = useState<number | null>(null)

  useLayoutEffect(() => {
    if (!isDetailRow || isPinned) return

    const measure = () => {
      const el = ref.current
      if (!el || !params.node) return

      const height = Math.ceil(el.scrollHeight)
      const nextHeight = Math.min(Math.max(height, 88), DETAIL_ROW_MAX_HEIGHT + 16)
      if (params.node.rowHeight !== nextHeight) {
        params.node.setRowHeight(nextHeight)
        params.api?.onRowHeightChanged()
      }
    }

    measure()
    const frame = window.requestAnimationFrame(measure)
    return () => window.cancelAnimationFrame(frame)
  }, [isDetailRow, isPinned, logins, loginsLoading, loginsError, params.node, params.api])

  useLayoutEffect(() => {
    if (!isDetailRow || isPinned || !logins?.length) {
      setTimeLeft(null)
      return
    }

    const applyLeft = (left: number | null) => {
      setTimeLeft((current) => (current === left ? current : left))
    }

    const alignTime = () => {
      const panel = ref.current
      if (!panel) return
      alignRecentLoginTime(panel, applyLeft)
    }

    alignTime()
    const frame = window.requestAnimationFrame(alignTime)
    const api = params.api
    api?.addEventListener("columnResized", alignTime)
    api?.addEventListener("columnMoved", alignTime)
    api?.addEventListener("gridSizeChanged", alignTime)
    api?.addEventListener("bodyScroll", alignTime)
    api?.addEventListener("displayedColumnsChanged", alignTime)

    const headerCell = ref.current
      ?.closest(".ag-root")
      ?.querySelector(`.ag-header-cell[col-id="${RECENT_LOGIN_TIME_COL_ID}"]`)
    const observer = headerCell ? new ResizeObserver(alignTime) : null
    if (headerCell) observer?.observe(headerCell)

    return () => {
      window.cancelAnimationFrame(frame)
      api?.removeEventListener("columnResized", alignTime)
      api?.removeEventListener("columnMoved", alignTime)
      api?.removeEventListener("gridSizeChanged", alignTime)
      api?.removeEventListener("bodyScroll", alignTime)
      api?.removeEventListener("displayedColumnsChanged", alignTime)
      observer?.disconnect()
    }
  }, [isDetailRow, isPinned, logins, params.api])

  if (isPinned || !isDetailRow || !row) return null

  const panelStyle: CSSProperties & { "--um-login-time-left"?: string } =
    timeLeft == null ? {} : { "--um-login-time-left": `${timeLeft}px` }

  return (
    <div
      ref={ref}
      className="um-recent-logins-panel border-b border-border/60 bg-muted/20 px-4 py-3"
      style={panelStyle}
      data-time-aligned={timeLeft == null ? undefined : "true"}
    >
      <span className="um-recent-logins-title">
        Recent Logins (Last {RECENT_LOGINS_DISPLAY_LIMIT})
      </span>
      {loginsLoading ? (
        <p className="mt-2 text-sm text-muted-foreground">Loading…</p>
      ) : loginsError ? (
        <p className="mt-2 text-sm text-destructive">{loginsError}</p>
      ) : !logins || logins.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">No recorded logins yet.</p>
      ) : (
        <ul className="um-recent-logins-list mt-2 pr-1">
          {logins.map((login, idx) => (
            <li key={idx}>
              <span className="um-recent-login-place">
                <BsGlobe className="shrink-0 text-muted-foreground" />
                <TruncatedText
                  text={login.location || login.ipAddress}
                  className="um-recent-login-location"
                />
                <span className="um-recent-login-ip">{login.ipAddress}</span>
              </span>
              <span className="um-recent-login-time">
                <BsClock /> {ctx.formatRelativeTime(login.occurredAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function buildUserGridRows(
  displayUsers: PlatformUserRow[],
  users: PlatformUserRow[],
  expandedRowKey: string | null,
  recentLoginsByKey: Record<string, RecentLogin[]>,
  recentLoginsLoadingKey: string | null,
  recentLoginsError: string | null,
): UserGridRow[] {
  const rows: UserGridRow[] = []
  displayUsers.forEach((u) => {
    const rowKey = `${u.userType}-${u.id}`
    const realIdx = users.findIndex((row) => `${row.userType}-${row.id}` === rowKey)
    rows.push({
      rowType: "user",
      rowKey,
      realIdx,
      createdAt: u.createdAt,
      updatedAt: u.updatedAt,
      createdBy: u.createdBy,
      updatedBy: u.updatedBy,
    })
    if (expandedRowKey === rowKey) {
      rows.push({
        rowType: "detail",
        rowKey,
        logins: recentLoginsByKey[rowKey],
        loginsLoading: recentLoginsLoadingKey === rowKey,
        loginsError: recentLoginsError,
      })
    }
  })
  return rows
}

type UserFilterColumnConfig = {
  key: UserColumnFilterKey
  label: string
  getOptions: (rows: PlatformUserRow[]) => string[]
}

export const USER_FILTER_COLUMNS: UserFilterColumnConfig[] = [
  {
    key: "name",
    label: USER_COLUMN_FILTER_LABELS.name,
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.name || "—")),
  },
  {
    key: "email",
    label: USER_COLUMN_FILTER_LABELS.email,
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.email || "—")),
  },
  {
    key: "role",
    label: USER_COLUMN_FILTER_LABELS.role,
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.role || "—")),
  },
  {
    key: "status",
    label: USER_COLUMN_FILTER_LABELS.status,
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.status || "Inactive")),
  },
  {
    key: "lastLogin",
    label: USER_COLUMN_FILTER_LABELS.lastLogin,
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.lastLoginRelative || "—")),
  },
]

export const USER_COLUMN_LABELS = Object.fromEntries(
  USER_FILTER_COLUMNS.map(({ key, label }) => [key, label]),
) as Record<UserColumnFilterKey, string>

function columnLabel(key: UserColumnFilterKey) {
  return USER_COLUMN_LABELS[key]
}

export function buildUserColumnFilterFields(sourceRows: PlatformUserRow[]) {
  return USER_FILTER_COLUMNS.map(({ key, label, getOptions }) => ({
    key,
    label,
    options: getOptions(sourceRows),
  }))
}

export function createUserColumnDefs(): ColDef<UserGridRow>[] {
  return [
    {
      colId: "name",
      headerName: columnLabel("name"),
      flex: 2,
      minWidth: 200,
      filterValueGetter: (params) =>
        userFilterValueGetter(params, (user) => user.name),
      cellRenderer: UserNameOrDetailCellRenderer,
    },
    {
      colId: "email",
      headerName: columnLabel("email"),
      flex: 1.5,
      minWidth: 200,
      filterValueGetter: (params) =>
        userFilterValueGetter(params, (user) => user.email),
      cellRenderer: UserEmailCellRenderer,
    },
    {
      colId: "merchantName",
      headerName: USER_MERCHANT_NAME_HEADER,
      flex: 1.5,
      minWidth: 180,
      filterValueGetter: (params) =>
        userFilterValueGetter(params, (user) => user.merchantName || ""),
      cellRenderer: UserMerchantNameCellRenderer,
    },
    {
      colId: "role",
      headerName: columnLabel("role"),
      flex: 1,
      minWidth: 140,
      filterValueGetter: (params) =>
        userFilterValueGetter(params, (user) => user.role),
      cellRenderer: UserRoleCellRenderer,
    },
    {
      colId: "status",
      headerName: columnLabel("status"),
      width: 100,
      filterValueGetter: (params) =>
        userFilterValueGetter(params, (user) => user.status),
      cellRenderer: UserStatusCellRenderer,
      pinned:'right'
    },
    {
      colId: "lastLogin",
      headerName: columnLabel("lastLogin"),
      flex: 1,
      minWidth: 150,
      filterValueGetter: (params) =>
        userFilterValueGetter(params, (user) => user.lastLoginRelative || ""),
      cellRenderer: UserLastLoginCellRenderer,
    },
    {
      headerName: "Actions",
      flex: 2,
      minWidth: 225,
      width: 225,
      cellRenderer: UserActionsCellRenderer,
      sortable: false,
      filter: false,
      pinned: "right",
    },
  ]
}
